/**
 * Connection Callbacks Factory
 * Creates the ChatConnection callback object for MarianaChat.
 * Keeps the connectedCallback in mariana-chat.ts lean.
 */
import { type ChatConnectionCallbacks } from './chat-connection.js';
import { type ChatMessage, type ButtonOption, type Recipe, type Coupon, type Shop, type ImageItem } from './types.js';
import { createChatMessage } from '../../utils/chat-utils.js';
import { config } from '../../config/environment.js';
import { NetworkStateManager } from './network-state-manager.js';
import { buildAssistantMessagesFromLivePayload } from './message-adapter.js';

export interface ConnectionCallbacksContext {
    networkState: NetworkStateManager;
    source: string;
    getIsReconnecting: () => boolean;
    setWsConnected: (v: boolean) => void;
    setIsTyping: (v: boolean) => void;
    setIsResponding: (v: boolean) => void;
    setIsReconnecting: (v: boolean) => void;
    addMessage: (
        message: string,
        sender: string,
        direction: 'incoming' | 'outgoing',
        hierarchy_buttons?: ButtonOption[],
        normal_buttons?: ButtonOption[],
        list_buttons?: ButtonOption[],
        extras?: { csat?: ButtonOption[]; recipes?: Recipe[]; coupons?: Coupon[]; shops?: Shop[]; image?: ImageItem }
    ) => void;
    appendMessages: (messages: ChatMessage[]) => void;
    appendInitialMessage: (msg: ChatMessage) => void;
    markInitialMessageShown: () => void;
    setIsBufferingInitial: (v: boolean) => void;
    addOfflineMessage: () => void;
}

export function createConnectionCallbacks(ctx: ConnectionCallbacksContext): ChatConnectionCallbacks {
    let isBufferingInitial = false;
    const initialBuffer: ChatMessage[] = [];

    return {
        onConnected: () => {
            ctx.setWsConnected(true);
            ctx.networkState.markSessionEstablished();

            if (ctx.networkState.shouldShowInitialMessage(ctx.getIsReconnecting())) {
                if (ctx.source.toLowerCase() === 'col') {
                    // For COL, buffer all initial server messages until finished_all,
                    // then show the greeting + menu together to avoid a jarring 2-second gap.
                    isBufferingInitial = true;
                    ctx.setIsBufferingInitial(true);
                } else {
                    const msg = createChatMessage(config.MESSAGES.INITIAL_MESSAGE, 'assistant', 'incoming');
                    ctx.appendInitialMessage(msg);
                    ctx.networkState.markInitialMessageShown();
                }
            }
        },

        onDisconnected: () => {
            ctx.setWsConnected(false);
            ctx.setIsTyping(false);
            ctx.setIsResponding(false);
            ctx.setIsReconnecting(false);
        },

        onOffline: () => {
            ctx.setIsTyping(false);
            ctx.setIsResponding(false);
            ctx.setWsConnected(false);
            const { shouldShowMessage } = ctx.networkState.handleOffline();
            if (shouldShowMessage) {
                ctx.addOfflineMessage();
            }
        },

        onMessage: (_content: string, data?: unknown) => {
            if (ctx.networkState.shouldIgnoreMessage()) {
                return;
            }

            const normalized = buildAssistantMessagesFromLivePayload(data);

            if (isBufferingInitial) {
                initialBuffer.push(...normalized);
                return;
            }

            if (normalized.length > 0) {
                ctx.appendMessages(normalized);
            }

            ctx.setIsTyping(false);
        },

        onTyping: (isTyping: boolean) => {
            if (isBufferingInitial) return;
            if (ctx.networkState.shouldIgnoreTyping()) return;
            ctx.setIsTyping(isTyping);
        },

        onFinished: () => {
            ctx.setIsResponding(false);
        },

        onFinishedAll: () => {
            ctx.setIsResponding(false);

            if (isBufferingInitial) {
                isBufferingInitial = false;
                ctx.setIsBufferingInitial(false);
                const greetingMsg = createChatMessage(config.MESSAGES.COL_INITIAL_MESSAGE, 'assistant', 'incoming');
                ctx.appendMessages([greetingMsg, ...initialBuffer]);
                initialBuffer.length = 0;
                ctx.networkState.markInitialMessageShown();
            }
        },

        onError: () => {
            if (isBufferingInitial) {
                isBufferingInitial = false;
                ctx.setIsBufferingInitial(false);
                initialBuffer.length = 0;
                ctx.networkState.markInitialMessageShown();
            }
            if (ctx.networkState.shouldIgnoreError()) return;
            ctx.setIsTyping(false);
            ctx.setIsResponding(false);
            ctx.setIsReconnecting(false);
            ctx.addMessage(config.MESSAGES.GENERIC_ERROR_MESSAGE, 'system', 'incoming');
        },
    };
}
